import { action, makeAutoObservable, runInAction } from 'mobx'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api'

export type ThemeType = 'light' | 'dark' | 'system'
export type FontSizeType = 'small' | 'medium' | 'large'
export type LanguageType = 'en' | 'fr'
export type LanguageChangeHandler = (language: LanguageType) => void

// GraphQL queries
const GET_LLM_SETTINGS = gql`
  query getSettings {
    getSettings {
      temperature
      max_tokens
      selected_case
      fakeResponses
      language
      use_gpt_to_prompt
      token_limit
      temperature2
      example_policy
      time
      georgiaVoiceEnabled
      roleplayVoiceEnabled
      onboarded
      module
    }
  }
`

const UPDATE_LLM_SETTINGS = gql`
  mutation updateSettings($input: UpdateSettingsInput!) {
    updateSettings(input: $input) {
      temperature
      max_tokens
      selected_case
      fakeResponses
      language
      use_gpt_to_prompt
      token_limit
      temperature2
      example_policy
      time
      georgiaVoiceEnabled
      roleplayVoiceEnabled
      onboarded
      module
    }
  }
`

class SettingsStore {
  // UI settings
  theme: ThemeType = 'light'
  fontSize: FontSizeType = 'medium'
  notifications: boolean = true
  autoSave: boolean = true
  language: LanguageType = this.getInitialLanguage()

  // LLM settings
  temperature: number = 0.7
  max_tokens: number = 2048
  selected_case: number = 1
  fakeResponses: boolean = false
  use_gpt_to_prompt: boolean = false
  token_limit: number = 4000
  temperature2: number = 0.5
  example_policy: 'skip' | 'keep' | 'strong_keep' | 'smart' = 'skip'
  time: string = ''
  georgiaVoiceEnabled: boolean = false
  roleplayVoiceEnabled: boolean = false
  onboarded: boolean = false
  module: number | null = null

  // Status flags
  isLoading: boolean = false
  error: string | null = null
  settingsLoaded: boolean = false

  // Language change callback
  private onLanguageChangeHandler: LanguageChangeHandler | null = null

  constructor() {
    // Use makeAutoObservable with configuration to maintain proper 'this' binding
    makeAutoObservable(
      this,
      {
        fetchLLMSettings: action,
        setTemperature: action,
        setMaxTokens: action,
        setSelectedCase: action,
        setFakeResponses: action,
        setLanguage: action,
        setUseGptToPrompt: action,
        setTokenLimit: action,
        setTemperature2: action,
        setExamplePolicy: action,
        setTime: action,
        setGeorgiaVoiceEnabled: action,
        setRoleplayVoiceEnabled: action,
        setOnboarded: action,
        setModule: action,
      },
      { autoBind: true },
    )
    this.fetchLLMSettings()
  }

  private getInitialLanguage(): LanguageType {
    // // Get browser language
    // const browserLang = navigator.language || navigator.languages[0]

    // // Extract the primary language code (e.g., 'en' from 'en-US')
    // const primaryLang = browserLang.split('-')[0].toLowerCase()

    // // Check if the browser language is supported
    // if (primaryLang === 'fr') {
    //   return 'fr'
    // }

    // Default to English for all other languages
    return 'en'
  }

  // LLM Settings Methods
  setTemperature(value: number) {
    this.temperature = value
  }

  setMaxTokens(value: number) {
    this.max_tokens = value
  }

  setSelectedCase(value: number) {
    this.selected_case = value
  }

  setFakeResponses(value: boolean) {
    this.fakeResponses = value
  }

  setUseGptToPrompt(value: boolean) {
    this.use_gpt_to_prompt = value
  }

  setTokenLimit(value: number) {
    this.token_limit = value
  }

  setTemperature2(value: number) {
    this.temperature2 = value
  }

  setExamplePolicy(value: 'skip' | 'keep' | 'strong_keep' | 'smart') {
    this.example_policy = value
  }

  setTime(value: string) {
    this.time = value
  }

  setGeorgiaVoiceEnabled(value: boolean) {
    this.georgiaVoiceEnabled = value
  }

  setRoleplayVoiceEnabled(value: boolean) {
    this.roleplayVoiceEnabled = value
  }

  setOnboarded(value: boolean) {
    this.onboarded = value
  }

  setModule(value: number | null) {
    this.module = value
  }

  setLanguage(value: LanguageType) {
    this.language = value
    // Notify any listeners about the language change
    if (this.onLanguageChangeHandler) {
      this.onLanguageChangeHandler(value)
    }
  }

  // Method to set the language change handler
  setOnLanguageChange(handler: LanguageChangeHandler) {
    this.onLanguageChangeHandler = handler
  }

  setLoading(value: boolean) {
    this.isLoading = value
  }

  async fetchLLMSettings() {
    try {
      this.setLoading(true)
      this.error = null
      const data: {
        getSettings?: {
          temperature: number
          max_tokens: number
          selected_case: number
          fakeResponses: boolean
          language?: LanguageType
          use_gpt_to_prompt?: boolean
          token_limit?: number
          temperature2?: number
          example_policy?: 'skip' | 'keep' | 'strong_keep' | 'smart'
          time?: string
          georgiaVoiceEnabled?: boolean
          roleplayVoiceEnabled?: boolean
          onboarded?: boolean
          module?: string | null
        }
      } = await authorizedGraphQLRequest(GET_LLM_SETTINGS)

      if (data.getSettings) {
        runInAction(() => {
          this.temperature = data.getSettings!.temperature
          this.max_tokens = data.getSettings!.max_tokens
          this.selected_case = data.getSettings!.selected_case
          this.fakeResponses = data.getSettings!.fakeResponses ?? false
          // Only update language if backend provides it
          if (data.getSettings!.language) {
            this.language = data.getSettings!.language
          }
          this.use_gpt_to_prompt = data.getSettings!.use_gpt_to_prompt ?? false
          this.token_limit = data.getSettings!.token_limit ?? 4000
          this.temperature2 = data.getSettings!.temperature2 ?? 0.5
          this.example_policy = data.getSettings!.example_policy ?? 'skip'
          this.time = data.getSettings!.time ?? ''
          this.georgiaVoiceEnabled = data.getSettings!.georgiaVoiceEnabled ?? false
          this.roleplayVoiceEnabled = data.getSettings!.roleplayVoiceEnabled ?? false
          this.onboarded = data.getSettings!.onboarded ?? false
          // Normalize module: numeric string → number, legacy strings → null
          const rawModule = data.getSettings!.module
          if (rawModule != null && !isNaN(Number(rawModule))) {
            this.module = parseInt(rawModule, 10)
          } else {
            this.module = null
          }
          this.settingsLoaded = true
          if (this.onLanguageChangeHandler) {
            this.onLanguageChangeHandler(this.language)
          }
        })
      }
    } catch (err) {
      console.error('Failed to fetch LLM settings:', err)
      this.error = 'Failed to load settings'
    } finally {
      this.setLoading(false)
    }
  }

  async saveLLMSettings() {
    try {
      this.setLoading(true)
      this.error = null

      const input = {
        temperature: this.temperature,
        max_tokens: this.max_tokens,
        selected_case: this.selected_case,
        fakeResponses: this.fakeResponses,
        language: this.language,
        use_gpt_to_prompt: this.use_gpt_to_prompt,
        token_limit: this.token_limit,
        temperature2: this.temperature2,
        example_policy: this.example_policy,
        time: new Date().toISOString(),
        georgiaVoiceEnabled: this.georgiaVoiceEnabled,
        roleplayVoiceEnabled: this.roleplayVoiceEnabled,
        onboarded: this.onboarded,
        module: this.module != null ? String(this.module) : null,
      }

      await authorizedGraphQLRequest(UPDATE_LLM_SETTINGS, { input })
    } catch (err) {
      console.error('Failed to save LLM settings:', err)
      this.error = 'Failed to save settings'
      throw err // Re-throw to allow component to handle redirect
    } finally {
      this.setLoading(false)
    }
  }

  async updateSettings(
    updates: Partial<{
      temperature: number
      max_tokens: number
      selected_case: number
      fakeResponses: boolean
      language: LanguageType
      use_gpt_to_prompt: boolean
      token_limit: number
      temperature2: number
      example_policy: 'skip' | 'keep' | 'strong_keep' | 'smart'
      time: string
      georgiaVoiceEnabled: boolean
      roleplayVoiceEnabled: boolean
      onboarded: boolean
      module: number | null
    }>,
  ) {
    try {
      this.setLoading(true)
      this.error = null

      const input = {
        ...updates,
        module: updates.module !== undefined ? (updates.module != null ? String(updates.module) : null) : undefined,
        time: new Date().toISOString(),
      }

      const data = await authorizedGraphQLRequest(UPDATE_LLM_SETTINGS, { input })

      if (data.updateSettings) {
        runInAction(() => {
          const { module: rawModule, ...rest } = data.updateSettings
          Object.assign(this, rest)
          // Normalize module: numeric string → number, legacy strings → null
          if (rawModule != null && !isNaN(Number(rawModule))) {
            this.module = parseInt(rawModule, 10)
          } else {
            this.module = null
          }
        })
      }

      return data.updateSettings
    } catch (err) {
      console.error('Failed to update settings:', err)
      this.error = 'Failed to update settings'
      throw err
    } finally {
      this.setLoading(false)
    }
  }
}

// Create a singleton instance that will be provided via context
export const settingsStore = new SettingsStore()
